import { motion } from "framer-motion";
import {
  Layers,
  Globe2,
  Building2,
  Boxes,
  CalendarRange,
  Film,
} from "lucide-react";
import SectionLabel from "./SectionLabel";

const CAPABILITIES = [
  {
    n: "01",
    icon: Building2,
    label: "EXHIBITION & STALL",
    title: "Concept to installation - complete ownership.",
    items: [
      "3D stall design and visualisation",
      "Fabrication and on-ground execution",
      "Brand boards, displays, panels",
      "Prototype and product mockups",
      "Brochures, leaflets, collaterals",
      "LED screen content & animations",
    ],
    diff: "A fully delivered, ready-to-operate exhibition experience.",
  },
  {
    n: "02",
    icon: Boxes,
    label: "RETAIL & RCIP OUTLETS",
    title: "Scalable retail identity systems across locations.",
    items: [
      "RCIP (Retail Common Identity) design",
      "Standardised branding across outlets",
      "Reception, furniture, display units",
      "Dealer / distributor boards",
      "LED screens & digital integration",
      "Design → Build → Install → Maintain",
    ],
    diff: "Structural durability with 3-year assurance and ongoing maintenance.",
  },
  {
    n: "03",
    icon: Globe2,
    label: "WEBSITE & DIGITAL",
    title: "From concept to launch - fully managed.",
    items: [
      "Website / app UI–UX from scratch",
      "Content built around the offering",
      "Conversion-focused structure & CTAs",
      "Animated and motion assets",
      "Corporate PPT → website transformation",
      "End-to-end development & deployment",
    ],
    diff: "Turning static corporate information into engaging, enquiry-generating platforms.",
  },
  {
    n: "04",
    icon: Film,
    label: "CONTENT & MEDIA",
    title: "Visual storytelling that supports the brand.",
    items: [
      "Corporate presentations",
      "Product and factory shoots",
      "Live-action product videos",
      "Animated and AI-generated videos",
      "Digital content for marketing",
      "Exhibition and event content",
    ],
    diff: "Production capability that plugs directly into the rest of the system.",
  },
  {
    n: "05",
    icon: Layers,
    label: "BRANDING & IDENTITY",
    title: "From idea to identity - fully built and deployed.",
    items: [
      "Brand name creation",
      "Logo and identity systems",
      "Stationery and corporate kit",
      "Merchandise branding",
      "Product branding & packaging",
      "Brochures, leaflets, catalogues",
    ],
    diff: "Not just design - complete brand thinking applied across every material.",
  },
  {
    n: "06",
    icon: CalendarRange,
    label: "EVENTS & DEALER MEETS",
    title: "Structured, brand-led event execution.",
    items: [
      "Concept and program design",
      "Destination selection",
      "Stage branding & event identity",
      "Merchandise and collateral",
      "Video content for events",
      "End-to-end coordination & logistics",
    ],
    diff: "Selective, high-involvement engagements - not volume-driven.",
  },
];

const ease = [0.22, 0.61, 0.36, 1];

const cardVariants = {
  hidden: { opacity: 0, y: 36 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: (i % 3) * 0.08, ease },
  }),
};

const titleWordVariants = {
  hidden: { y: "110%", opacity: 0 },
  visible: (i) => ({
    y: "0%",
    opacity: 1,
    transition: { duration: 0.6, delay: 0.25 + i * 0.04, ease },
  }),
};

const bulletVariants = {
  hidden: { x: -12, opacity: 0 },
  visible: (i) => ({
    x: 0,
    opacity: 1,
    transition: { duration: 0.45, delay: 0.5 + i * 0.07, ease },
  }),
};

const labelLineVariants = {
  hidden: { scaleX: 0 },
  visible: { scaleX: 1, transition: { duration: 0.7, delay: 0.15, ease } },
};

const diffUnderlineVariants = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: 0.9, delay: 1.05, ease },
  },
};

const CapabilityCard = ({ cap, index }) => {
  const Icon = cap.icon;
  const titleWords = cap.title.split(" ");

  return (
    <motion.div
      custom={index}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-80px" }}
      whileHover={{
        y: -6,
        rotateX: 1.5,
        rotateY: -1.5,
        transition: { duration: 0.4, ease },
      }}
      style={{ transformPerspective: 1000, transformStyle: "preserve-3d" }}
      className="group bg-[#CCD6DC] border border-black/30 p-7 md:p-8 relative transition-colors hover:border-[#AE519F]/70"
      data-testid={`capability-card-${cap.n}`}
    >
      {/* corner ticks - draw in on hover */}
      <motion.span
        className="absolute top-0 left-0 w-3 h-3 origin-top-left"
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
      >
        <span className="absolute top-0 left-0 w-3 h-px bg-black" />
        <span className="absolute top-0 left-0 w-px h-3 bg-black" />
      </motion.span>
      <div className="absolute top-0 left-0 w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <span className="absolute top-0 left-0 w-3 h-px bg-black" />
        <span className="absolute top-0 left-0 w-px h-3 bg-black" />
      </div>
      <div className="absolute top-0 right-0 w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-75">
        <span className="absolute top-0 right-0 w-3 h-px bg-black" />
        <span className="absolute top-0 right-0 w-px h-3 bg-black" />
      </div>
      <div className="absolute bottom-0 left-0 w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-150">
        <span className="absolute bottom-0 left-0 w-3 h-px bg-black" />
        <span className="absolute bottom-0 left-0 w-px h-3 bg-black" />
      </div>
      <div className="absolute bottom-0 right-0 w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-200">
        <span className="absolute bottom-0 right-0 w-3 h-px bg-black" />
        <span className="absolute bottom-0 right-0 w-px h-3 bg-black" />
      </div>

      {/* Top row: number + icon */}
      <div className="flex items-start justify-between mb-8">
        <motion.div
          className="mono-label inline-block origin-left"
          whileHover={{ scale: 1.06 }}
          transition={{ duration: 0.3, ease }}
        >
          <motion.span
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.05, ease }}
            className="inline-block"
          >
            {`/ ${cap.n}`}
          </motion.span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.6, rotate: -25 }}
          whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.18, ease }}
          whileHover={{ rotate: 8, scale: 1.12 }}
          className="text-black/70 group-hover:text-[#AE519F] transition-colors"
        >
          <Icon size={22} strokeWidth={1.4} />
        </motion.div>
      </div>

      {/* Mono label with draw-in underline */}
      <div className="mb-4">
        <div className="mono-label group-hover:text-[#AE519F] transition-colors">
          {cap.label}
        </div>
        <motion.div
          className="mt-2 h-px bg-black origin-left"
          variants={labelLineVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          style={{ width: "32px" }}
        />
      </div>

      {/* Title with word-by-word reveal */}
      <h3
        className="text-black text-2xl md:text-[26px] mb-6 group-hover:text-[#AE519F] transition-colors"
        style={{
          fontFamily: "Archivo, sans-serif",
          fontWeight: 800,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
        }}
      >
        {titleWords.map((w, i) => (
          <span
            key={`${w}-${i}`}
            className="inline-block overflow-hidden align-bottom mr-[0.25em]"
          >
            <motion.span
              custom={i}
              variants={titleWordVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="inline-block"
            >
              {w}
            </motion.span>
          </span>
        ))}
      </h3>

      {/* Bullets with staggered reveal + colored dash on hover */}
      <ul className="space-y-2.5 mb-7">
        {cap.items.map((it, i) => (
          <motion.li
            key={it}
            custom={i}
            variants={bulletVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="flex items-start gap-3 text-black/80 text-[15px] hover:text-black transition-colors"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            <motion.span
              className="mt-2.5 h-px bg-black flex-shrink-0 origin-left group-hover:bg-[#AE519F]"
              initial={{ width: 0 }}
              whileInView={{ width: 12 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.55 + i * 0.07, ease }}
            />
            <span>{it}</span>
          </motion.li>
        ))}
      </ul>

      {/* Differentiator with draw-in underline */}
      <div className="pt-5 border-t border-black/30 relative">
        <motion.div
          className="absolute -top-px left-0 h-px bg-black origin-left"
          variants={diffUnderlineVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          style={{ width: "48px" }}
        />
        <div className="mono-label mb-2">DIFFERENTIATOR</div>
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 1.15, ease }}
          className="text-black/85 text-[14px] leading-relaxed"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {cap.diff}
        </motion.p>
      </div>
    </motion.div>
  );
};

const CoreCapabilities = () => {
  return (
    <section
      id="capabilities"
      data-testid="capabilities-section"
      className="relative py-24 md:py-32 border-t-2 border-black"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-7">
            <SectionLabel className="mb-6" testid="label-capabilities">
              [ 03 ] - CORE CAPABILITIES
            </SectionLabel>
            <h2
              className="font-archivo font-black h-tighter text-4xl md:text-5xl lg:text-6xl text-black"
              style={{ fontFamily: "Archivo, sans-serif" }}
            >
              Six execution domains.
              <br />
              <span className="text-[#AE519F]">One operating system.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              className="text-black/70 text-base md:text-lg max-w-md"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              We don't list services. We run a connected practice. Each domain
              below is a module - designed to interoperate with the rest.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-px bg-black/30">
          {CAPABILITIES.map((cap, i) => (
            <CapabilityCard key={cap.n} cap={cap} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CoreCapabilities;
